import { useMemo } from 'react'
import { BoardView } from '@/components/kanban/board-view'
import { ViewHeader } from '@/components/shell/view-header'
import { useOpenIssueSheet } from '@/hooks/use-ui-state'
import { useViewFilters } from '@/hooks/use-view-filters'
import { fieldByName, useIssueFields, useIssues } from '@/lib/query'
import { priorityIndex } from '@/lib/stage'
import { SHIP_VIEW } from '@/lib/views'
import { AppIcon } from '@/components/common/icon-map'
import type { WorkIssue } from '@/types/github'

/** Merged + Released, columns by Product. Read-only — releases move these. */
export default function Ship() {
  const query = useIssues()
  const issues = query.data
  const fields = useIssueFields().data
  const openIssue = useOpenIssueSheet()
  const chipFilter = useViewFilters()

  const products = useMemo(
    () => fieldByName(fields, 'Product')?.options.map((o) => o.name) ?? [],
    [fields],
  )

  const itemsByColumn = useMemo(() => {
    const pool = chipFilter.apply(SHIP_VIEW.pool(issues ?? [], {}))
    const map = new Map<string, WorkIssue[]>()
    const none = 'no product'
    for (const p of products) map.set(p, [])
    map.set(none, [])
    for (const issue of pool) {
      const key = issue.product && map.has(issue.product) ? issue.product : none
      map.get(key)!.push(issue)
    }
    // Merged → QA → live within a column (legacy Released shares the live tier).
    const stageRank = (s: string | null) =>
      s === 'Merged' ? 0 : s === 'Released to QA' ? 1 : 2
    for (const list of map.values()) {
      list.sort(
        (a, b) =>
          stageRank(a.stage) - stageRank(b.stage) ||
          priorityIndex(a.priority) - priorityIndex(b.priority),
      )
    }
    if (map.get(none)!.length === 0) map.delete(none)
    return map
  }, [issues, chipFilter, products])

  const total = [...itemsByColumn.values()].reduce((n, list) => n + list.length, 0)

  return (
    <div className="flex h-full flex-col">
      <ViewHeader
        title="About to ship"
        meta={`${total} items moving toward release · grouped by Product · Merged → QA → live`}
        chips={['priority', 'client']}
      />
      <div className="min-h-0 flex-1">
        <BoardView
          query={query}
          skeletonColumns={4}
          columns={[...itemsByColumn.keys()].map((p) => ({
            key: p,
            title: p,
            glyph: <AppIcon name="package" className="text-muted-foreground size-3.5" />,
          }))}
          itemsByColumn={itemsByColumn}
          onCardClick={openIssue}
        />
      </div>
    </div>
  )
}
